/**
 * The single source of truth for the cross-surface header switcher: the three
 * SystemDox surfaces in canonical lifecycle order (Capture → Build → Knowledge Base)
 * with their display labels and icon artwork.
 *
 * Consumed by the React `SurfaceSwitcher` (via `surfaceLinks()` in apps/app)
 * across the capture app, the tenant console and the in-app Knowledge Base
 * reader. (The static docs portal that once also rendered this file was retired
 * in ADR-032 — one authenticated surface, no separate published site.)
 *
 * Change the order, a label, or an icon HERE and every surface follows. URL
 * construction stays runtime-specific (surfaceLinks() derives the tenant URLs
 * from auth/hostname), so only the presentation data lives here.
 *
 * That presentation data also carries the lifecycle copy — each surface's
 * `tagline` and the `handoff` into the next one — so the launch page can
 * render Capture → Build → Knowledge Base as a pipeline without restating what the
 * switcher already knows.
 *
 * This module is the SINGLE SOURCE OF TRUTH for the surface names. The repo
 * used to carry several competing vocabularies for these stages — the
 * marketing site sold "Capture / Orchestrate / Knowledge / Insights", and the
 * architecture guide sold a "Define → Build → Verify → Learn" loop in which
 * Verify sat beside Build. Both are retired: the homepage, `/features` and
 * `/guides/architecture` now import from here, so a name can only change in
 * one place. Anything new that describes the surfaces should read from here
 * rather than restate them.
 *
 * Icons are lucide path data (Smartphone / LayoutDashboard / Globe), inlined so
 * every renderer shows the identical artwork without a client runtime. The
 * Knowledge Base is a GLOBE, not a book: below 64rem the switcher is icon-only,
 * and the glyph is then the only thing telling a reader that this surface is
 * the documentation shared across the whole organisation (as against a private
 * notebook). Render inside:
 *   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
 *        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
 */

export type SurfaceKey = 'app' | 'console' | 'docs';

export interface Surface {
  key: SurfaceKey;
  label: string;
  /** Inner SVG markup (lucide path data) for a 24x24 stroke icon. */
  icon: string;
  /** One-line statement of what this surface is for. */
  tagline: string;
  /**
   * What flows from this surface into the next one, phrased as the label on
   * the connector between them. Undefined on the last surface — nothing sits
   * downstream of the Knowledge Base.
   *
   * Keep these honest about how the handoff actually works. Capture → Build is
   * not a passive landing: createDoc writes into the same tenant spec store the
   * console reads, and then captureHost's onOpenDocument immediately sends the
   * browser cross-host into the Build editor for the doc it just created. You
   * do not "find it in Build later" — you are already there.
   *
   * Build → Knowledge Base is the opposite, and stays an explicit,
   * human-triggered hop (docs → a git repo's markdown tree, committed to
   * GitHub), never automatic — hence "publish", never a bare arrow. GitHub is
   * the source of truth; the Knowledge Base reads it live, so there is no
   * separate site to build. The connector says WHAT has to happen, not which
   * surface you press it from.
   */
  handoff?: string;
}

export const SURFACES: readonly Surface[] = [
  {
    key: 'app',
    label: 'Capture',
    icon: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
    tagline:
      'Add requirements and designs by voice, photo, file or text — then browse, gap-check and publish the whole doc library.',
    handoff: 'opens straight in the editor',
  },
  {
    key: 'console',
    label: 'Build',
    icon: '<rect width="7" height="9" x="3" y="3" rx="1"/><rect width="7" height="5" x="14" y="3" rx="1"/><rect width="7" height="9" x="14" y="12" rx="1"/><rect width="7" height="5" x="3" y="16" rx="1"/>',
    tagline:
      'What your AI reads — the standards, specs and plans your agents build on. Execution happens in your own AI tool, over MCP.',
    handoff: 'only on an explicit publish',
  },
  {
    key: 'docs',
    label: 'Knowledge Base',
    icon: '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
    tagline:
      'Your organisation’s documentation, read live from the markdown in your git repos — authenticated, and scoped to each member’s access.',
  },
];
